import type { NutrientProfile } from "../nutrition/nutrients";
import { scaleNutrients, validateNutrientProfile } from "../nutrition/nutrients";
import { caloriesFromNutrients } from "../nutrition/energy";
import { requireFinitePositive } from "../shared/validation";

export type FoodMeasureUnit = "g" | "ml" | "serving";

export const FOOD_MEASURE_UNITS = ["g", "ml", "serving"] as const satisfies readonly FoodMeasureUnit[];
export const FOOD_ID_MAX_LENGTH = 200;
export const FOOD_NAME_MAX_LENGTH = 500;
export const FOOD_BRAND_MAX_LENGTH = 500;
export const FOOD_BARCODE_MAX_LENGTH = 100;
export const FOOD_SERVING_NAME_MAX_LENGTH = 200;
export const FOOD_MEASURE_ID_MAX_LENGTH = 100;
export const FOOD_MEASURE_LABEL_MAX_LENGTH = 200;

export interface FoodBasis {
  readonly amount: number;
  readonly unit: FoodMeasureUnit;
  readonly servingName?: string;
}

/** One provider-supplied measure expressed in the food's canonical basis unit. */
export interface FoodMeasure {
  readonly id: string;
  readonly label: string;
  /** Canonical basis units represented by exactly one of this measure. */
  readonly amount: number;
}

export type FoodProvenance =
  | { readonly kind: "custom" }
  | { readonly kind: "photo-estimate" }
  | {
      readonly kind: "imported";
      readonly provider?: string;
      readonly providerFoodId?: string;
      readonly importedAt?: string;
    };

export interface Food {
  readonly id: string;
  readonly name: string;
  readonly brand?: string;
  readonly barcode?: string;
  readonly basis: FoodBasis;
  readonly measures?: readonly FoodMeasure[];
  /** Nutrients for exactly one `basis` quantity. Calories are derived, not stored. */
  readonly nutrients: NutrientProfile;
  readonly provenance: FoodProvenance;
  readonly preferred: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface FoodPortion {
  /** Canonical amount in `unit`; nutrition calculations never use the display measure directly. */
  readonly amount: number;
  readonly unit: FoodMeasureUnit;
  readonly display?: {
    readonly amount: number;
    readonly measureId: string;
    readonly label: string;
  };
}

export function validateFood(food: Food): Food {
  const id = requiredFoodText(food.id, "Food id", FOOD_ID_MAX_LENGTH);
  const name = requiredFoodText(food.name, "Food name", FOOD_NAME_MAX_LENGTH);
  const brand = optionalFoodText(food.brand, "Food brand", FOOD_BRAND_MAX_LENGTH);
  const barcode = optionalFoodText(food.barcode, "Food barcode", FOOD_BARCODE_MAX_LENGTH);
  return {
    ...food,
    id,
    name,
    brand,
    barcode,
    basis: validateFoodBasis(food.basis),
    ...(food.measures === undefined ? {} : { measures: validateFoodMeasures(food.measures) }),
    nutrients: validateNutrientProfile(food.nutrients),
  };
}

export function validateFoodBasis(basis: FoodBasis): FoodBasis {
  if (basis === null || typeof basis !== "object") throw new Error("Food basis is required");
  requireFinitePositive(basis.amount, "Food basis amount");
  if (!FOOD_MEASURE_UNITS.includes(basis.unit)) {
    throw new Error(`Unsupported food basis unit: ${String(basis.unit)}`);
  }
  const servingName = optionalFoodText(basis.servingName, "Serving name", FOOD_SERVING_NAME_MAX_LENGTH);
  if (basis.unit === "serving" && !servingName) {
    throw new Error("A serving-based food requires a serving name");
  }
  return { amount: basis.amount, unit: basis.unit, servingName };
}

export function validateFoodMeasures(measures: readonly FoodMeasure[]): readonly FoodMeasure[] {
  if (!Array.isArray(measures)) throw new Error("Food measures must be a list");
  const seen = new Set<string>();
  return measures.map((measure) => {
    if (measure === null || typeof measure !== "object") throw new Error("Food measure is required");
    const id = requiredFoodText(measure.id, "Food measure id", FOOD_MEASURE_ID_MAX_LENGTH);
    const label = requiredFoodText(measure.label, "Food measure label", FOOD_MEASURE_LABEL_MAX_LENGTH);
    requireFinitePositive(measure.amount, "Food measure amount");
    if (seen.has(id)) throw new Error(`Food measure id must be unique: ${id}`);
    seen.add(id);
    return { id, label, amount: measure.amount };
  });
}

export function validateFoodPortion(
  portion: FoodPortion,
  basis?: FoodBasis,
  measures?: readonly FoodMeasure[],
): FoodPortion {
  if (portion === null || typeof portion !== "object") throw new Error("Food portion is required");
  requireFinitePositive(portion.amount, "Portion amount");
  if (!FOOD_MEASURE_UNITS.includes(portion.unit)) {
    throw new Error(`Unsupported food portion unit: ${String(portion.unit)}`);
  }
  if (basis && portion.unit !== validateFoodBasis(basis).unit) {
    throw new Error(`Cannot convert ${portion.unit} to ${basis.unit} without an explicit conversion`);
  }
  const validatedMeasures = measures === undefined ? undefined : validateFoodMeasures(measures);
  if (portion.display === undefined) return { amount: portion.amount, unit: portion.unit };

  if (portion.display === null || typeof portion.display !== "object") {
    throw new Error("Food portion display is invalid");
  }
  requireFinitePositive(portion.display.amount, "Food portion display amount");
  const measureId = requiredFoodText(
    portion.display.measureId,
    "Food portion display measure id",
    FOOD_MEASURE_ID_MAX_LENGTH,
  );
  const label = requiredFoodText(
    portion.display.label,
    "Food portion display label",
    FOOD_MEASURE_LABEL_MAX_LENGTH,
  );
  if (validatedMeasures === undefined) throw new Error("A displayed food measure requires food measures");
  const measure = validatedMeasures.find((candidate) => candidate.id === measureId);
  if (!measure) throw new Error(`Food portion refers to an unknown measure: ${measureId}`);
  if (label !== measure.label) throw new Error("Food portion display label does not match its measure");
  const convertedAmount = portion.display.amount * measure.amount;
  const tolerance = Math.max(1e-9, Math.abs(portion.amount) * 1e-9);
  if (Math.abs(convertedAmount - portion.amount) > tolerance) {
    throw new Error("Food portion display does not match its canonical amount");
  }
  return {
    amount: portion.amount,
    unit: portion.unit,
    display: { amount: portion.display.amount, measureId, label },
  };
}

export function nutrientsForPortion(food: Food, portion: FoodPortion): NutrientProfile {
  const basis = validateFoodBasis(food.basis);
  const validatedPortion = validateFoodPortion(portion, basis, food.measures);
  return scaleNutrients(food.nutrients, validatedPortion.amount / basis.amount);
}

export function caloriesForPortion(food: Food, portion: FoodPortion): number {
  return caloriesFromNutrients(nutrientsForPortion(food, portion));
}

function requiredFoodText(value: string, label: string, maximumLength: number): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(`${label} cannot be empty`);
  const trimmed = value.trim();
  if (trimmed.length > maximumLength) throw new Error(`${label} cannot exceed ${maximumLength} characters`);
  return trimmed;
}

function optionalFoodText(value: string | undefined, label: string, maximumLength: number): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string") throw new Error(`${label} must be text`);
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.length > maximumLength) throw new Error(`${label} cannot exceed ${maximumLength} characters`);
  return trimmed;
}
